import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIcon } from '@angular/material/icon'
import {
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderToastService,
  Interval,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Instant } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { DataPoint } from '../../../../charts/data-point'
import { MexusViewMode } from '../../../../context/view-mode-context/mexus-view-mode'
import {
  buildConstraint,
  Constraint,
} from '../../../../shopfloor/workcenters/capability/constraints/constraint'
import { ConstraintInput } from '../../../../shopfloor/workcenters/capability/constraints/constraintInput'
import { WorkCenter } from '../../../../shopfloor/workcenters/work-center'
import { UtilisationCapacityBarComponent } from '../../utilisation-bar/utilisation-capacity-bar/utilisation-capacity-bar.component'
import { UtilisationFilterBuilder } from '../../utilisation-filter-builder'
import { WCOrderAggregate, WorkCenterUtilisation } from '../../work-center-utilisation'
import { WorkCenterUtilisationService } from '../../work-center-utilisation.service'
import { WorkCenterOrderDetailComponent } from '../work-center-order-detail/work-center-order-detail.component'
import { WorkCenterOrdersComponent } from '../work-center-orders/work-center-orders.component'

export class WCDetailUtilisationCtx {
  public static readonly Empty = new WCDetailUtilisationCtx(null, [])

  public static from(wcUtil: WorkCenterUtilisation): WCDetailUtilisationCtx {
    let data: DataPoint<string, number>[] = []

    if (wcUtil.stateAggregate) {
      data = [
        new DataPoint<string, number>(
          'context.completed',
          wcUtil.stateAggregate.completed.totalWeight.value
        ),
        new DataPoint<string, number>(
          'context.processing',
          wcUtil.stateAggregate.progress.totalWeight.value
        ),
        new DataPoint<string, number>('context.open', wcUtil.stateAggregate.open.totalWeight.value),
      ]
    }

    return new WCDetailUtilisationCtx(wcUtil, data)
  }

  constructor(
    public readonly wcUtil: WorkCenterUtilisation | null,
    public readonly utilisationStatus: DataPoint<string, number>[]
  ) {}
}

@Component({
  selector: 'app-work-center-utilisation-detail',
  templateUrl: './work-center-utilisation-detail.component.html',
  styleUrls: ['./work-center-utilisation-detail.component.scss'],
  imports: [
    MatIcon,
    UtilisationCapacityBarComponent,
    WorkCenterOrdersComponent,
    WorkCenterOrderDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
  ],
})
export class WorkCenterUtilisationDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _workCenter$ = new BehaviorSubject<WorkCenter>(null)
  private readonly _scheduleInterval$ = new BehaviorSubject<Interval>(null)
  private readonly _viewMode$ = new BehaviorSubject<MexusViewMode>(null)
  private readonly _activeOrderId$ = new BehaviorSubject<string>(null)
  private readonly _itemConstraints$ = new BehaviorSubject<ConstraintInput[]>(null)
  private readonly _refreshContext$ = new BehaviorSubject<void>(null)
  public readonly utilisationCtx$ = new BehaviorSubject<WCDetailUtilisationCtx>(null)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const wcUtilisationService = inject(WorkCenterUtilisationService)
    const toastService = inject(ElderToastService)
    combineLatest([
      this._workCenter$,
      this._scheduleInterval$,
      this._viewMode$,
      this._itemConstraints$,
      this._refreshContext$,
    ])
      .pipe(
        takeUntilDestroyed(),
        switchMap(([wc, interval, viewMode, itemConstraints]) => {
          if (wc && interval && viewMode && itemConstraints) {
            return wcUtilisationService.findWorkCenterUtilisation(
              wc.id,
              UtilisationFilterBuilder.start()
                .viewMode(viewMode)
                .scheduleInterval(interval)
                .timestamp(Instant.now()) // TODO
                .build(),
              this.buildConstraints(itemConstraints)
            )
          } else {
            return of(null)
          }
        })
      )
      .subscribe({
        next: (utilCtx) =>
          this.utilisationCtx$.next(
            utilCtx ? WCDetailUtilisationCtx.from(utilCtx) : WCDetailUtilisationCtx.Empty
          ),
        error: (err) => toastService.error('Failed to load W/C Utilisation due error!', err),
      })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get workCenter$(): Observable<WorkCenter> {
    return this._workCenter$.asObservable()
  }

  public get workCenter(): WorkCenter {
    return this._workCenter$.getValue()
  }

  @Input()
  public set workCenter(wc: WorkCenter) {
    this._workCenter$.next(wc)
  }

  @Input()
  public set scheduleInterval(interval: Interval) {
    this._scheduleInterval$.next(interval)
  }

  @Input()
  public set viewMode(viewMode: MexusViewMode) {
    this._viewMode$.next(viewMode)
  }

  public get activeOrderId$(): Observable<string> {
    return this._activeOrderId$.asObservable()
  }

  public get activeOrderId(): string {
    return this._activeOrderId$.getValue()
  }

  public set activeOrderId(activeOrder: string) {
    this._activeOrderId$.next(activeOrder)
  }

  public get itemConstraints$(): Observable<ConstraintInput[]> {
    return this._itemConstraints$.asObservable()
  }

  public get itemConstraints(): ConstraintInput[] {
    return this._itemConstraints$.getValue()
  }

  @Input()
  public set itemConstraints(constraints: ConstraintInput[]) {
    this._itemConstraints$.next(constraints)
  }

  /***************************************************************************
   *                                                                         *
   * Public Api                                                              *
   *                                                                         *
   **************************************************************************/

  public showOrderDetail(order: WCOrderAggregate): void {
    if (order) {
      this.activeOrderId = order.workOrderId
    }
  }

  public onContextRefreshRequest(): void {
    this._refreshContext$.next()
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildConstraints(constraints: ConstraintInput[]): Constraint[] {
    if (!!constraints) {
      return constraints.map((constraintInput) => buildConstraint(constraintInput))
    }
    return []
  }
}
